import type { AppState } from '../state/default-state';
import {
  selectBodyView,
  selectLegendRows,
  selectOrbitalReadouts,
  selectSectorSettings
} from '../state/selectors';
import type { BodyStatus } from '../state/selectors';
import { describeOrbitShape } from './format';

/**
 * Presentation-layer text alternative for the canvas (AD-8). Names the central
 * body and every orbiting body with its status, so assistive technology gets a
 * complete summary of the scene the canvas draws. Refreshed on state change.
 * English copy lives at the ui boundary, not in core (AD-5).
 */

function statusWord(status: BodyStatus): string {
  switch (status) {
    case 'active':
      return 'active';
    case 'collided':
      return 'collided';
    case 'absorbed':
      return 'absorbed';
  }
}

export function describeSystem(state: AppState): string {
  const central = selectBodyView(state, state.centralBody);
  const orbiting = selectLegendRows(state)
    .filter((row) => !row.isCentral)
    .map((row) => {
      const selected = row.isSelected ? ', selected' : '';
      return `${row.name} (${statusWord(row.status)}${selected})`;
    });

  const motion = state.paused ? 'paused' : 'playing';

  // Reflect the selected body's orbit shape so screen-reader users perceive the
  // eccentricity change that sighted users see on canvas (AD-8). English copy
  // lives here in ui, not core (AD-5).
  const sel = selectBodyView(state, state.selectedBody);
  let selectedClause = ` Selected body: ${sel.name}.`;
  if (!sel.isCentral) {
    const e = selectOrbitalReadouts(state, state.selectedBody).e;
    const shape = describeOrbitShape(e);
    selectedClause = ` Selected body: ${sel.name}, ${shape} orbit, eccentricity ${e.toFixed(2)}.`;
  }

  // Reflect the equal-time-sector overlay so screen-reader users perceive it
  // without relying on colour alone (AC 5, AD-8). Only when it is on for a
  // non-central active orbiter. English copy lives here in ui, not core (AD-5).
  const sectorSettings = selectSectorSettings(state);
  let sectorClause = '';
  if (sectorSettings.visible && sectorSettings.applicable) {
    sectorClause = ` Equal-time sectors are on for ${sel.name}: ${sectorSettings.count} sectors of equal time and approximately equal area.`;
  }

  // Reflect the active body-scale mode (Story 3.3, FR-12) so the tiny-body meaning
  // stays available in accessible text, not colour or size alone (AD-8). English
  // copy lives here in ui, not core (AD-5).
  const scaleClause =
    state.scaleMode === 'realistic'
      ? ' Body scale: realistic (true relative sizes; planets appear very small, each marked by a position marker and named in the legend).'
      : ' Body scale: readable (bodies enlarged for clarity).';

  return (
    `Solar system view. Central body: ${central.name}. ` +
    `${orbiting.length} orbiting bodies: ${orbiting.join(', ')}. ` +
    `The simulation is ${motion}.` +
    selectedClause +
    sectorClause +
    scaleClause
  );
}
